import React from 'react';
import {
  ArrowRight,
  AlertTriangle,
  BriefcaseBusiness,
  Building2,
  Check,
  FileCheck2,
  Layers3,
  MessageSquareText,
  Palette,
  Radar,
  ShieldCheck,
  Sparkles,
  UserRound,
  UsersRound,
  WalletCards,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import rocImage from './roc.jpg';
import michaelImage from './michael.png';
import peiqiImage from './peiqi.png';
import './BusinessPlanPage.css';

type AudienceSegment = {
  label: string;
  title: string;
  body: string;
  icon: LucideIcon;
};

type PainColumn = {
  eyebrow: string;
  title: string;
  summary: string;
  points: Array<{
    label: string;
    title: string;
    body: string;
  }>;
};

type SolutionStep = {
  index: string;
  title: string;
  from: string;
  to: string;
  icon: LucideIcon;
  body: string;
  result: string;
};

type ProductFlowStage = {
  label: string;
  caption: string;
  page: string;
  buyerAction: string;
  creatorAction: string;
};

type ProductPage = {
  index: string;
  title: string;
  subtitle: string;
  role: string;
  icon: LucideIcon;
  body: string;
  wireframe: string[];
};

type MarketLayer = {
  label: string;
  value: string;
  caption: string;
  color: string;
  size: string;
  bottom: string;
};

type FinanceMetric = {
  label: string;
  value: string;
  caption: string;
};

type TrendSignal = {
  label: string;
  value: string;
  caption: string;
};

type OrderMixRow = {
  type: string;
  share: string;
  avgPrice: string;
  monthlyOrders: string;
  monthlyGmv: string;
  width: string;
  color: string;
};

type RevenueStream = {
  type: string;
  share: string;
  annual: string;
  monthly: string;
  assumption: string;
  color: string;
};

type SubscriptionPlan = {
  type: string;
  userBase: string;
  conversion: string;
  monthlyFee: string;
  paidUsers: string;
  features: string[];
};

type CostItem = {
  item: string;
  cost: string;
  width: string;
  group: string;
};

type ProfitMetric = {
  label: string;
  value: string;
  caption: string;
};

type CompetitionRow = {
  category: string;
  metric: string;
  cloud: string;
  overseas: string;
  traditional: string;
  vertical: string;
};

type CompetitiveConclusion = {
  target: string;
  win: string;
  body: string;
  proofs: string[];
};

type LaunchPhase = {
  index: string;
  phase: string;
  analogy: string;
  duration: string;
  highlight: string;
  goal: string;
  narrative: string;
  actions: string[];
  channels: string[];
  result: string;
};

type FounderProfile = {
  title: string;
  image: string;
  imagePosition: string;
  lines: string[];
  responsibility: string;
};

type Milestone = {
  time: string;
  title: string;
  ops: string;
  finance: string;
  variant?: 'funding' | 'growth' | 'product';
};

type MilestoneMarker = {
  milestoneIndex: number;
  x: string;
  y: string;
  labelX: string;
  labelY: string;
  width: string;
};

type FundingRound = {
  name: string;
  amount: string;
  equity: string;
  valuation: string;
  goal: string;
};

type FundUse = {
  label: string;
  amount: number;
};

type ThemeVars = React.CSSProperties & Record<`--${string}`, string>;

const businessPlanTheme: ThemeVars = {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei', sans-serif",
    '--bp-display-font': "'Inter', -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei', sans-serif",
    '--bp-page-bg': 'oklch(0.965 0.006 240)',
    '--bp-frame': 'oklch(0.995 0.002 230)',
    '--bp-panel': 'oklch(0.988 0.004 232)',
    '--bp-surface': 'oklch(0.996 0.002 230)',
    '--bp-surface-strong': 'oklch(0.972 0.006 235)',
    '--bp-tint': 'oklch(0.945 0.018 232)',
    '--bp-tint-strong': 'oklch(0.972 0.012 220)',
    '--bp-bar': 'oklch(0.91 0.011 235)',
    '--bp-text': 'oklch(0.21 0.025 248)',
    '--bp-text-strong': 'oklch(0.14 0.025 250)',
    '--bp-text-muted': 'oklch(0.43 0.026 248)',
    '--bp-text-subtle': 'oklch(0.58 0.022 248)',
    '--bp-on-accent': 'oklch(0.985 0.004 230)',
    '--bp-on-accent-soft': 'oklch(0.88 0.018 235)',
    '--bp-accent': 'oklch(0.23 0.026 250)',
    '--bp-accent-strong': 'oklch(0.16 0.03 250)',
    '--bp-accent-deep': 'oklch(0.36 0.09 252)',
    '--bp-accent-soft': 'oklch(0.91 0.035 218)',
    '--bp-accent-softer': 'oklch(0.965 0.012 220)',
    '--bp-accent-text': 'oklch(0.34 0.065 248)',
    '--bp-accent-muted': 'oklch(0.43 0.05 236)',
    '--bp-border': 'oklch(0.87 0.012 240)',
    '--bp-border-soft': 'oklch(0.9 0.01 240)',
    '--bp-border-strong': 'oklch(0.82 0.018 240)',
    '--bp-accent-border': 'oklch(0.74 0.04 232)',
    '--bp-accent-border-strong': 'oklch(0.58 0.055 242)',
    '--bp-grid': 'oklch(0.86 0.014 232)',
    '--bp-grid-strong': 'oklch(0.7 0.05 226)',
    '--bp-decor-border': 'oklch(0.86 0.012 240)',
    '--bp-warning': 'oklch(0.55 0.17 25)',
    '--bp-warning-strong': 'oklch(0.46 0.16 24)',
    '--bp-warning-soft': 'oklch(0.965 0.03 35)',
    '--bp-warning-tint': 'oklch(0.95 0.04 38)',
    '--bp-warning-border': 'oklch(0.76 0.07 35)',
    '--bp-chart-1': 'oklch(0.72 0.08 225)',
    '--bp-chart-2': 'oklch(0.61 0.12 244)',
    '--bp-chart-3': 'oklch(0.28 0.05 250)',
    '--bp-chart-4': 'oklch(0.46 0.08 255)',
    '--bp-chart-5': 'oklch(0.9 0.03 82)',
    '--bp-path-1': 'oklch(0.72 0.08 225)',
    '--bp-path-2': 'oklch(0.28 0.05 250)',
    '--bp-path-3': 'oklch(0.46 0.08 255)',
    '--bp-path-soft': 'oklch(0.84 0.04 226)',
    '--bp-path-highlight': 'oklch(0.99 0.003 230)',
    '--bp-shadow-frame': 'oklch(0.24 0.025 250 / 0.14)',
    '--bp-shadow-soft': 'oklch(0.24 0.025 250 / 0.1)',
    '--bp-shadow-faint': 'oklch(0.24 0.025 250 / 0.06)',
    '--bp-shadow-accent': 'oklch(0.38 0.06 250 / 0.16)',
    '--bp-shadow-accent-soft': 'oklch(0.38 0.06 250 / 0.09)',
    '--bp-shadow-warm': 'oklch(0.48 0.12 35 / 0.12)',
    '--bp-shadow-pin': 'oklch(0.24 0.025 250 / 0.18)',
    '--bp-shadow-diagram': 'oklch(0.38 0.06 250 / 0.16)',
    '--bp-path-shadow': 'oklch(0.38 0.06 250)',
  };

const buyerSegments: AudienceSegment[] = [
  {
    label: '01',
    title: '各行各业的商家 / 企业',
    body: '品牌方外包广告宣传片，短剧工作室需要 AI 漫剧承制，淘宝店主做商品图，自媒体公司做 AI 动效。',
    icon: Building2,
  },
  {
    label: '02',
    title: '普通个人用户',
    body: '找人指导设计作业的大学生，定制个人专属生日祝福视频的普通人。',
    icon: UserRound,
  },
  {
    label: '03',
    title: '接单太多的包工头',
    body: '自己接了项目但忙不过来的创作者或工作室，需要把部分工作拆出去交付。',
    icon: BriefcaseBusiness,
  },
];

const creatorSegments: AudienceSegment[] = [
  {
    label: '01',
    title: '个人创作者',
    body: '熟练掌握 AIGC 工具的自由职业者、接单兼职达人，需要把能力转化成稳定商业订单。',
    icon: Sparkles,
  },
  {
    label: '02',
    title: '专业工作室',
    body: '专业团队，专门承接复杂、大型商业交付项目的创意机构。',
    icon: UsersRound,
  },
];

const transactionSteps = ['找对人', '保交付', '稳支付'];

const painColumns: PainColumn[] = [
  {
    eyebrow: '需求方痛点',
    title: '需求方：找人难，管人难',
    summary: '缺少垂直、可信、可比较的 AIGC 创作者入口，项目一旦进入私聊，进度和成本就很难控制。',
    points: [
      {
        label: '01',
        title: '寻源低效，成本与匹配度不透明',
        body: '个人无处寻源，企业只能在内容社区盲搜或微信群海投；熟人和传统代理又带来重抽佣、报价不透明、无法横向比价。',
      },
      {
        label: '02',
        title: '沟通错位，交付进度失控',
        body: '缺乏标准化发包引导，需求表达容易偏差；遇到延期或拖延时，买家缺少平台约束，只能被动等待。',
      },
    ],
  },
  {
    eyebrow: '创作者痛点',
    title: '创作者：接单难，收钱难',
    summary: '创作者有作品和工具能力，但缺少稳定商业曝光、精准线索和第三方资金保障。',
    points: [
      {
        label: '03',
        title: '获客被动，线索与需求不精准',
        body: '严重依赖自媒体发帖等风来或微信群蹲单，客户常常需求模糊、预算不匹配，时间被无效沟通消耗。',
      },
      {
        label: '04',
        title: '履约弱势，尾款与心血无保障',
        body: '缺乏第三方资金约束，交付后可能遭遇压价、拖欠尾款、初稿拒付；维权成本高，争议难被处理。',
      },
    ],
  },
];

const solutionSteps: SolutionStep[] = [
  {
    index: '01',
    title: '撮合',
    from: '看见人',
    to: '找对人',
    icon: Radar,
    body: '集中展示作品、可承接项目、报价区间、工期、交付经验和服务评价，让需求方在沟通前完成初步筛选。',
    result: '减少“能不能做、多少钱、多久交付”的前期消耗。',
  },
  {
    index: '02',
    title: '交付',
    from: '私聊推进',
    to: '系统履约',
    icon: FileCheck2,
    body: '围绕项目签订线上合同，明确交付内容、时间节点、验收标准和修改范围，并按里程碑追踪双方动作。',
    result: '降低需求跑偏、延期交付和反复扯皮。',
  },
  {
    index: '03',
    title: '支付',
    from: '私下转账',
    to: '平台托管',
    icon: WalletCards,
    body: '项目资金先托管，阶段验收后释放款项；延期、争议或付款纠纷由平台介入协调和仲裁。',
    result: '保障需求方拿到约定成果，也保障创作者按约收款。',
  },
];

const productFlowStages: ProductFlowStage[] = [
  {
    label: '浏览展示',
    caption: '作品、作者、需求集中展示',
    page: '首页',
    buyerAction: '先看作品与作者',
    creatorAction: '浏览最新和最热需求',
  },
  {
    label: '筛选查找',
    caption: '把模糊想法整理成可报价需求',
    page: '发现页',
    buyerAction: '按预算、风格、工期找创作者',
    creatorAction: '按照自己擅长类别找需求',
  },
  {
    label: '沟通方案',
    caption: '方案、报价、试稿沉淀在项目上下文',
    page: '聊天页',
    buyerAction: '确认方案、报价和试稿',
    creatorAction: '提交方案并锁定边界',
  },
  {
    label: '签约交付',
    caption: '合同、里程碑、验收标准在线确认',
    page: '试稿与合同',
    buyerAction: '小步验证后签约',
    creatorAction: '按阶段提交交付物',
  },
  {
    label: '托管支付',
    caption: '资金按阶段释放，争议有平台介入',
    page: '合同管理',
    buyerAction: '验收后释放款项',
    creatorAction: '按约收款并沉淀评价',
  },
];

const productPages: ProductPage[] = [
  {
    index: '01',
    title: '首页',
    subtitle: '建立信任与激发交易',
    role: '交易入口',
    icon: Sparkles,
    body: '第一屏快速说明平台定位，随后用创作者榜单、作品展示、作者展示和需求展示，让双方看到真实供需密度。',
    wireframe: ['定位', '榜单', '作品', '作者', '需求'],
  },
  {
    index: '02',
    title: '发现页面',
    subtitle: '高效筛选人与需求',
    role: '匹配引擎',
    icon: Radar,
    body: '需求方按领域、价格、工期、风格和工具能力筛选创作者；创作者按预算、交付类型和需求状态发现可接订单。',
    wireframe: ['筛选器', '作品卡', '作者卡', '需求卡'],
  },
  {
    index: '03',
    title: '聊天页面',
    subtitle: '从沟通进入交易',
    role: '交易工作台',
    icon: MessageSquareText,
    body: '聊天不只是私信，而是项目推进入口。双方在同一上下文中提交方案、确认报价、发起试稿和发起合同。',
    wireframe: ['会话', '方案', '报价', '动作区'],
  },
  {
    index: '04',
    title: '试稿管理',
    subtitle: '降低首次合作风险',
    role: '验证机制',
    icon: FileCheck2,
    body: '试稿让双方在正式签约前验证能力和需求匹配度，页面同时追踪付款、交付、预览、审核反馈和转正状态。',
    wireframe: ['状态', '预览', '反馈', '转合同'],
  },
  {
    index: '05',
    title: '发起合同',
    subtitle: '标准化项目约定',
    role: '规则生成',
    icon: BriefcaseBusiness,
    body: '双方可以新建线上合同，也可以上传已有合同文件，并在系统中设置里程碑、验收标准、修改次数和阶段付款比例。',
    wireframe: ['条款', '里程碑', '付款', '验收'],
  },
  {
    index: '06',
    title: '合同管理',
    subtitle: '项目履约中枢',
    role: '控制台',
    icon: ShieldCheck,
    body: '执行期间，合同管理页追踪托管付款、阶段交付、文件预览、审核截止日期、纠纷记录、发票和打款记录。',
    wireframe: ['进度', '交付', '资金', '争议'],
  },
];

const marketLayers: MarketLayer[] = [
  {
    label: '可服务市场',
    value: '7,200亿',
    caption: '2028 年中国 AIGC 产业规模，数据来源：量子位智库',
    color: 'var(--bp-chart-1)',
    size: '100%',
    bottom: '0%',
  },
  {
    label: '可获得市场',
    value: '750亿',
    caption: '剔除企业内部消化，约 15% 内容需求外溢至外部承接方',
    color: 'var(--bp-chart-2)',
    size: '78%',
    bottom: '0%',
  },
  {
    label: '目标 GMV',
    value: '7.5亿',
    caption: '目标渗透率 1%，仅切核心外包盘的最优份额',
    color: 'var(--bp-chart-3)',
    size: '52%',
    bottom: '0%',
  },
  {
    label: '核心净营收',
    value: '7500万',
    caption: '按综合 10% 抽佣率测算',
    color: 'var(--bp-chart-4)',
    size: '30%',
    bottom: '0%',
  },
];

const financeMetrics: FinanceMetric[] = [
  {
    label: '目标渗透率',
    value: '1%',
    caption: '切取核心外包盘的最优份额',
  },
  {
    label: '目标 GMV',
    value: '7.5亿',
    caption: '人民币 / 年',
  },
  {
    label: '核心净营收',
    value: '7,500万',
    caption: '按综合 10% 抽佣率',
  },
  {
    label: '活跃创作者',
    value: '1.5万',
    caption: '仅需渗透约 0.6% 创作者',
  },
];

const trendSignals: TrendSignal[] = [
  {
    label: '渗透率参照',
    value: '1.5%',
    caption: '猪八戒网 10 年在传统外包市场的渗透率约 1.5%，三年 1% 是保守目标。',
  },
  {
    label: '全球趋势',
    value: '$30亿+',
    caption: 'Upwork 披露 AI 相关自由职业工作的年化交易价值超过 30 亿美元，年增长超过 200%。',
  },
  {
    label: '日本平台',
    value: '2.3 到 3 倍',
    caption: 'Lancers 和 CrowdWorks 数据显示，2024 年以来 AI 相关案件数同比增长 2.3 到 3 倍。',
  },
  {
    label: '国内平台',
    value: '350%',
    caption: '猪八戒网、一品威客等平台数据显示，2026 年第一季度 AI 相关订单量同比增长 350%。',
  },
];

const orderMixRows: OrderMixRow[] = [
  {
    type: '轻量创作单',
    share: '55%',
    avgPrice: '800 元',
    monthlyOrders: '18,750 单',
    monthlyGmv: '1500 万',
    width: '55%',
    color: 'var(--bp-chart-3)',
  },
  {
    type: '中型项目单',
    share: '35%',
    avgPrice: '3500 元',
    monthlyOrders: '8,000 单',
    monthlyGmv: '2800 万',
    width: '35%',
    color: 'var(--bp-chart-2)',
  },
  {
    type: '高客单项目',
    share: '10%',
    avgPrice: '1.5 万元',
    monthlyOrders: '258 单',
    monthlyGmv: '387.5 万',
    width: '10%',
    color: 'var(--bp-chart-1)',
  },
];

const revenueStreams: RevenueStream[] = [
  {
    type: '交易抽佣',
    share: '75%',
    annual: '5,625 万',
    monthly: '468.75 万',
    assumption: '撮合 GMV 5.625 亿，10% 平均抽佣',
    color: 'var(--bp-chart-3)',
  },
  {
    type: '订阅服务',
    share: '12%',
    annual: '900 万',
    monthly: '75 万',
    assumption: '创作者工具订阅 + 需求方协作订阅',
    color: 'var(--bp-chart-2)',
  },
  {
    type: '广告及其他',
    share: '8%',
    annual: '600 万',
    monthly: '50 万',
    assumption: '推广位、需求置顶、认证、精选服务包',
    color: 'var(--bp-chart-1)',
  },
  {
    type: '官方工作室直营',
    share: '5%',
    annual: '375 万',
    monthly: '31.25 万',
    assumption: '高客单项目直营交付，按净收入口径计算',
    color: 'var(--bp-chart-5)',
  },
];

const subscriptionPlans: SubscriptionPlan[] = [
  {
    type: '创作者订阅',
    userBase: '1.5 万活跃创作者',
    conversion: '60%',
    monthlyFee: '50 元/月',
    paidUsers: '9,000 人',
    features: ['AI 接单助手', '数据分析', '工作室多账号分工'],
  },
  {
    type: '需求方订阅',
    userBase: '约 1 万活跃需求方',
    conversion: '30%',
    monthlyFee: '99 元/月',
    paidUsers: '3,000 人',
    features: ['需求置顶延时', '团队协作', '私有创作者池'],
  },
];

const costItems: CostItem[] = [
  { item: '支付通道费', cost: '120 万', width: '30%', group: '交易成本' },
  { item: '云服务等基础设施', cost: '120 万', width: '30%', group: '基础设施' },
  { item: 'AI 调用成本', cost: '100 万', width: '25%', group: '基础设施' },
  { item: '客服、审核、质检、仲裁', cost: '100 万', width: '25%', group: '履约保障' },
  { item: '交易损耗与赔付准备', cost: '100 万', width: '25%', group: '履约保障' },
  { item: '产品团队 8 到 10 人', cost: '400 万', width: '100%', group: '团队' },
  { item: '运营团队 5 到 8 人', cost: '100 万', width: '25%', group: '团队' },
  { item: '企业 BD / 客户成功', cost: '160 万', width: '40%', group: '增长' },
  { item: '市场获客', cost: '200 万', width: '50%', group: '增长' },
  { item: '管理、财务、法务、人事、办公', cost: '100 万', width: '25%', group: '管理' },
];

const profitMetrics: ProfitMetric[] = [
  { label: 'GMV', value: '7.5 亿', caption: '年度总交易额' },
  { label: '平台收入', value: '7,500 万', caption: '核心净营收' },
  { label: '成本与费用', value: '1,500 万', caption: '年度总成本' },
  { label: 'EBITDA', value: '6,000 万', caption: '收入扣除成本费用' },
  { label: '净利润', value: '5,000 万', caption: '保守税费与折旧后' },
  { label: '利润率', value: '66%', caption: '净利润 / 平台收入' },
];

const competitionRows: CompetitionRow[] = [
  {
    category: '平台功能',
    metric: '撮合效率',
    cloud: '创作者橱窗化 + AI 需求解析，纯线上匹配，零电销打扰。',
    overseas: '算法成熟，但跨语言、时差和支付沟通成本高。',
    traditional: '依赖电话推销和低价竞标，优质创作者流失。',
    vertical: '企划招募与橱窗结合，但主要服务 2D 圈层。',
  },
  {
    category: '平台功能',
    metric: '交付辅助',
    cloud: 'AIGC 原生工作流，支持 4K 视频、3D 模型、提示词和源文件结构化交付。',
    overseas: '常规附件和任务管理为主，缺少 AIGC 专属流程。',
    traditional: '系统老旧，交付体验接近传统 OA。',
    vertical: '美术领域体验成熟，草稿、线稿、分阶段确认能力强。',
  },
  {
    category: '平台功能',
    metric: '支付与合同',
    cloud: '资金托管、细颗粒度里程碑、企业级 NDA、国内增值税专票支持。',
    overseas: '托管强，但国内税务发票、中文合同和本土法务适配弱。',
    traditional: '有基础托管与合同模板，但争议执行体验不稳定。',
    vertical: '标准化合同和企划金冻结成熟，但领域边界窄。',
  },
  {
    category: '市场生态',
    metric: '市场定位',
    cloud: 'AIGC 垂直专业工作站，服务现代数字创意与 AI 生产力变现。',
    overseas: '全球泛品类综合平台，覆盖翻译、IT、行政等大量品类。',
    traditional: '传统综合外包，低端设计、起名、开发等供给混杂。',
    vertical: '二次元、游戏美术垂直社区，社区心智清晰。',
  },
  {
    category: '市场生态',
    metric: '客户细分',
    cloud: '高价值 B 端与精英创作者，覆盖短剧出海、电商 TP、广告公司、AI 工作室。',
    overseas: '欧美中小微企业为主，国内 B 端使用门槛高。',
    traditional: '价格敏感的小微企业和下沉市场需求。',
    vertical: '游戏研发、二次元自媒体和插画师。',
  },
  {
    category: '商业模式',
    metric: '健康度',
    cloud: '克制抽佣 + 阶梯激励 + 双端订阅，交易与工具双轨驱动。',
    overseas: '抽佣较高，国内用户支付、税务和平台门槛明显。',
    traditional: '高抽佣叠加电销费用，甲方和创作者双侧体验恶化。',
    vertical: '生态健康但天花板受 2D 美术圈层限制。',
  },
];

const competitiveConclusions: CompetitiveConclusion[] = [
  {
    target: '对标海外巨头',
    win: '赢在本土合规与 AIGC 垂直生态',
    body: '海外平台交易机制成熟，但在中国 B 端市场存在财税、支付、合同和中文沟通的本土化断点。云作把这些交易障碍前置解决，同时针对 AIGC 交付构建原生工作流。',
    proofs: ['增值税专票', '中文 NDA', '本土支付链路', 'AI 原生交付'],
  },
  {
    target: '对标传统威客',
    win: '赢在品牌调性与极致体验',
    body: '传统威客平台被低价竞标和重度电销拖入廉价劳务心智，难以吸引顶尖创作者。云作用创作者橱窗化、无电销干扰和现代化交易体验，保护创作者定价权。',
    proofs: ['零电销打扰', '创作者橱窗', '定价权保护', '高端品牌心智'],
  },
  {
    target: '对标垂直标杆',
    win: '赢在赛道升维与跨行业天花板',
    body: '米画师验证了专业垂直工具加担保交易的高转化与高粘性。云作承袭这套闭环逻辑，但面向短剧、电商视觉、商业 TVC、3D 资产等更高频和高客单价的 AIGC 增量市场。',
    proofs: ['短剧出海', '商业 TVC', '电商视觉', '3D 资产'],
  },
];

const launchPhases: LaunchPhase[] = [
  {
    index: '01',
    phase: '种子验证',
    analogy: '上车',
    duration: '1 个月',
    highlight: '10 个订单',
    goal: '跑通最小交易闭环',
    narrative: '收集第一批真实需求，精准邀请匹配创作者，在平台引导下完成沟通、合同、里程碑交付和托管支付。',
    actions: ['收集 10 个真实需求', '邀请 10 位匹配创作者', '平台引导报价、签约、交付和托管支付'],
    channels: ['熟人网络', '早期用户', 'AIGC 社群', '小红书 / B 站定向邀请'],
    result: '完成首批 10 个订单，发现产品和履约问题，沉淀第一批真实案例。',
  },
  {
    index: '02',
    phase: '强运营增长',
    analogy: '蹬车',
    duration: '3 个月',
    highlight: '100 位创作者',
    goal: '形成初步供需流动',
    narrative: '基于首批案例做内容传播和定向招募，同时从电商、MCN、自媒体、短剧团队和个人用户侧获取真实需求。',
    actions: ['对外传播首批交易案例', '定向招募创作者和工作室', '运营榜单、试单活动和案例内容'],
    channels: ['小红书、B 站、抖音', '高校与 AIGC 社群', '设计 / 影视工作室', '电商商家、MCN、短剧团队'],
    result: '稳定留存 100 位可接单创作者池，供给密度超过单体 AIGC 工作室。',
  },
  {
    index: '03',
    phase: '飞轮自增长',
    analogy: '骑起来',
    duration: '24 个月',
    highlight: '双边网络效应',
    goal: '供需飞轮进入自增长',
    narrative: '创作者数量、作品案例、价格透明和履约保障形成供给优势，吸引更多需求方下单，订单增长继续吸引创作者入驻。',
    actions: ['用供给丰富度吸引需求方', '用订单增长吸引创作者', '用履约保障和价格透明建立信任'],
    channels: ['搜索引擎收录', '内容案例传播', '客户转介绍', '创作者自传播', '行业榜单和平台数据背书'],
    result: '需求方选择更多、下单更安心；创作者获得稳定订单，平台进入双边网络自增长。',
  },
];

const founders: FounderProfile[] = [
  {
    title: 'CEO Roc 万礼鹏',
    image: rocImage,
    imagePosition: '50% 48%',
    lines: [
      '本科新加坡国立生命科学',
      '硕士佐治亚理工计算机科学',
      '前亚马逊云服务RDS团队、亚马逊全球物流团队软件工程师，6 年开发经验。2025 年 6 月离职后，曾跟随国内头部AIGC导演团队创作6个月，后兼职担任a1 Lab工作室AIGC制片人，并成立小茉莉工作室担任AIGC制片人。',
    ],
    responsibility: '负责公司战略、产品方向和早期交易闭环落地。',
  },
  {
    title: 'CTO Michael 杨耿',
    image: michaelImage,
    imagePosition: '50% 42%',
    lines: [
      '本科中南大学自动化',
      '硕士布朗大学计算机工程',
      '亚马逊全球物流团队软件工程师，6 年以上研发经验，深耕大规模分布式系统与生成式 AI 应用，目前专注于基于 LLM 的多智能体系统研发。',
    ],
    responsibility: '主导核心业务流的全栈研发落地。',
  },
  {
    title: 'CMO 张佩琦',
    image: peiqiImage,
    imagePosition: '50% 30%',
    lines: [
      '前盒马用户运营，前钱大妈营销活动主管，前粗门小程序运营负责人',
      '专注运营用户社群、用户开发。将粗门从 0 用户做到百万注册用户。',
    ],
    responsibility: '负责用户开发和运营，以及产品商业化落地。',
  },
];

const milestones: Milestone[] = [
  {
    time: '第 0 天',
    title: '最小版本上线',
    ops: '作品展示、需求发布、聊天、合同、里程碑、支付托管可用',
    finance: '暂无收入目标',
    variant: 'product',
  },
  {
    time: '第 10 天',
    title: '30 位早期创作者',
    ops: '10 个核心创作者深度陪跑，20 个测试创作者丰富供给池',
    finance: '暂无收入目标',
    variant: 'growth',
  },
  {
    time: '第 30 天',
    title: '10 个测试需求',
    ops: '完成 10 个真实测试订单，收集案例和反馈',
    finance: '累计 GMV 2 到 5 万',
    variant: 'growth',
  },
  {
    time: '第 45 天',
    title: '天使轮融资',
    ops: '用最小版本和首批订单证明闭环可跑',
    finance: '融资 60 万，出让 8%，投后 750 万',
    variant: 'funding',
  },
  {
    time: '第 30 到 365 天',
    title: '全力宣传',
    ops: '案例传播、创作者招募、需求方 BD、社群运营、品类测试',
    finance: '从测试订单过渡到稳定月 GMV',
    variant: 'growth',
  },
  {
    time: '第 60 天',
    title: '创作者 60 人',
    ops: '累计完成 20 到 30 单',
    finance: '累计 GMV 5 到 10 万，月收入 0.3 到 0.8 万',
    variant: 'growth',
  },
  {
    time: '第 100 天',
    title: '创作者 100 人',
    ops: '累计完成 50 到 100 单，沉淀 10 到 20 个案例',
    finance: '月 GMV 10 到 30 万，月抽佣 1 到 3 万',
    variant: 'growth',
  },
  {
    time: '第 101 天',
    title: '种子轮融资',
    ops: '用 100 位创作者 + 早期交易数据启动增长',
    finance: '融资 300 万，出让 10%，投后 3000 万',
    variant: 'funding',
  },
  {
    time: '第 180 天',
    title: '模型厂商合作',
    ops: '接入模型，创作者 300 到 500 人，打穿 1 到 2 个高频品类',
    finance: '月 GMV 50 到 100 万，月收入 5 到 12 万',
    variant: 'product',
  },
  {
    time: '第 365 天',
    title: '创作者 1000 人',
    ops: '月活创作者 200 到 300 人，复购率 20% 到 30%',
    finance: '月 GMV 100 到 300 万，月收入 15 到 40 万',
    variant: 'growth',
  },
  {
    time: '第 365 天',
    title: 'Pre-A 轮融资',
    ops: '证明稳定交易、复购和初步商业化',
    finance: '融资 1000 到 2000 万，出让 10% 到 15%',
    variant: 'funding',
  },
  {
    time: '第 2 年',
    title: '创作者 5000 人',
    ops: '月活创作者 1000 到 1500 人，多品类稳定交易',
    finance: '年 GMV 1.5 到 2.5 亿，年收入 1500 到 2500 万',
    variant: 'growth',
  },
  {
    time: '第 3 年',
    title: '创作者 15000 人',
    ops: '稳定活跃创作者 8000 到 10000 人',
    finance: '年 GMV 5.625 亿，年收入 7500 万',
    variant: 'growth',
  },
];

const milestoneMarkers: MilestoneMarker[] = [
  { milestoneIndex: 0, x: '8%', y: '27%', labelX: '4%', labelY: '6%', width: '13rem' },
  { milestoneIndex: 1, x: '27%', y: '27%', labelX: '22%', labelY: '6%', width: '13rem' },
  { milestoneIndex: 2, x: '46%', y: '27%', labelX: '40%', labelY: '6%', width: '13rem' },
  { milestoneIndex: 3, x: '65%', y: '27%', labelX: '58%', labelY: '6%', width: '13rem' },
  { milestoneIndex: 4, x: '84%', y: '29%', labelX: '76%', labelY: '7%', width: '15rem' },
  { milestoneIndex: 5, x: '79%', y: '51%', labelX: '74%', labelY: '32%', width: '13rem' },
  { milestoneIndex: 6, x: '60%', y: '51%', labelX: '55%', labelY: '32%', width: '13rem' },
  { milestoneIndex: 7, x: '41%', y: '51%', labelX: '35%', labelY: '32%', width: '14rem' },
  { milestoneIndex: 8, x: '21%', y: '51%', labelX: '12%', labelY: '32%', width: '14rem' },
  { milestoneIndex: 9, x: '24%', y: '84%', labelX: '16%', labelY: '61%', width: '14rem' },
  { milestoneIndex: 10, x: '45%', y: '84%', labelX: '37%', labelY: '61%', width: '14rem' },
  { milestoneIndex: 11, x: '66%', y: '84%', labelX: '58%', labelY: '61%', width: '14rem' },
  { milestoneIndex: 12, x: '88%', y: '84%', labelX: '79%', labelY: '61%', width: '14rem' },
];

const fundingRounds: FundingRound[] = [
  {
    name: '天使轮',
    amount: '60 万',
    equity: '8%',
    valuation: '750 万',
    goal: '跑通首批交易，收集成功案例和宣传素材',
  },
  {
    name: '种子轮',
    amount: '300 万',
    equity: '10%',
    valuation: '3,000 万',
    goal: '扩大供需两端，提升月 GMV',
  },
];

const angelFundUses: FundUse[] = [
  { label: '产品开发', amount: 25 },
  { label: '创作者冷启动', amount: 10 },
  { label: '需求方获客与宣传', amount: 12 },
  { label: '运营与客服', amount: 5 },
  { label: '云服务、AI 调用、工具', amount: 3 },
  { label: '法务、财务及杂费', amount: 3 },
  { label: '机动资金', amount: 2 },
];

const seedFundUses: FundUse[] = [
  { label: '产品与技术团队', amount: 120 },
  { label: '市场获客与品牌宣传', amount: 70 },
  { label: '创作者运营', amount: 35 },
  { label: '企业 BD 与客户成功', amount: 35 },
  { label: '云服务、AI 调用、风控', amount: 15 },
  { label: '法务、财务、人事、办公', amount: 15 },
  { label: '机动资金', amount: 10 },
];

const SlideFrame: React.FC<{
  id: string;
  children: React.ReactNode;
  className?: string;
  innerClassName?: string;
}> = ({ id, children, className = '', innerClassName = '' }) => {
  const framePadding = id === 'cover' ? 'pt-32 pb-12' : 'py-12';

  return (
    <section id={id} className={`bp-slide-section relative scroll-mt-20 px-4 sm:px-6 lg:px-10 ${framePadding} ${className}`}>
      <div className={`bp-slide-frame relative mx-auto flex w-full max-w-[1520px] overflow-hidden border border-[var(--bp-border)] bg-[var(--bp-frame)] py-36 shadow-[0_24px_90px_var(--bp-shadow-frame)] ${innerClassName}`}>
        <Atmosphere />
        {children}
      </div>
    </section>
  );
};

const Atmosphere: React.FC = () => (
  <div aria-hidden="true" className="bp-atmosphere pointer-events-none absolute inset-0 overflow-hidden">
    <div className="bp-atmosphere-bg absolute inset-0 bg-[linear-gradient(135deg,var(--bp-panel)_0%,var(--bp-tint)_52%,var(--bp-frame)_100%)]" />
    <div className="bp-atmosphere-pattern absolute inset-0 opacity-[0.2] [background-image:linear-gradient(var(--bp-grid)_1px,transparent_1px),linear-gradient(90deg,var(--bp-grid)_1px,transparent_1px)] [background-size:72px_72px]" />
    <div className="bp-atmosphere-line bp-atmosphere-line-top absolute left-0 top-[18%] h-px w-full bg-[linear-gradient(90deg,transparent,var(--bp-grid-strong),transparent)] opacity-60" />
    <div className="bp-atmosphere-line bp-atmosphere-line-bottom absolute bottom-[14%] left-0 h-px w-full bg-[linear-gradient(90deg,transparent,var(--bp-grid),transparent)] opacity-80" />
    <div className="bp-atmosphere-low-band absolute inset-x-10 bottom-10 h-28 border-x border-t border-[var(--bp-decor-border)] opacity-45" />
    <div className="bp-atmosphere-large-block absolute -right-20 top-20 h-[560px] w-[560px] rotate-12 border border-[var(--bp-decor-border)] opacity-35" />
    <div className="bp-atmosphere-small-block absolute -left-24 bottom-16 h-[340px] w-[620px] -rotate-6 border border-[var(--bp-decor-border)] opacity-45" />
  </div>
);

const CoverDiagram: React.FC = () => (
  <div className="relative h-[480px] w-full max-w-[560px]">
    <div className="absolute left-10 right-10 top-16 h-px bg-[var(--bp-grid-strong)]" />
    <div className="absolute left-10 right-10 top-1/2 h-px bg-[var(--bp-grid-strong)]" />
    <div className="absolute left-10 right-10 bottom-16 h-px bg-[var(--bp-grid-strong)]" />
    <div className="absolute left-1/2 top-14 h-[350px] w-px -translate-x-1/2 bg-[var(--bp-grid-strong)]" />

    <div className="absolute left-1/2 top-1/2 z-10 w-56 -translate-x-1/2 -translate-y-1/2 border border-[var(--bp-accent-border-strong)] bg-[var(--bp-frame)] p-5 shadow-[0_22px_56px_var(--bp-shadow-diagram)]">
      <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">CraftCloud</p>
      <p className="mt-2 text-2xl font-black leading-tight text-[var(--bp-text-strong)]">AIGC 交易层</p>
      <div className="mt-5 grid gap-2">
        {transactionSteps.map((step) => (
          <div key={step} className="flex items-center justify-between border border-[var(--bp-border-soft)] bg-[var(--bp-accent-softer)] px-3 py-2">
            <span className="text-sm font-black text-[var(--bp-text)]">{step}</span>
            <Check className="h-4 w-4 text-[var(--bp-accent)]" />
          </div>
        ))}
      </div>
    </div>

    <div className="absolute left-0 top-6 w-44 border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] px-4 py-3 shadow-[0_14px_36px_var(--bp-shadow-soft)]">
      <p className="text-sm font-black text-[var(--bp-text)]">需求方</p>
      <p className="mt-1 text-xs font-semibold text-[var(--bp-text-muted)]">发布需求，看作品，确认方案</p>
    </div>
    <div className="absolute right-0 top-6 w-44 border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] px-4 py-3 shadow-[0_14px_36px_var(--bp-shadow-soft)]">
      <p className="text-sm font-black text-[var(--bp-text)]">创作者</p>
      <p className="mt-1 text-xs font-semibold text-[var(--bp-text-muted)]">展示能力，承接项目，按约收款</p>
    </div>
    <div className="absolute bottom-0 left-1/2 w-56 -translate-x-1/2 border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] px-4 py-3 text-center shadow-[0_14px_36px_var(--bp-shadow-soft)]">
      <p className="text-sm font-black text-[var(--bp-text)]">合同 · 里程碑 · 托管</p>
      <p className="mt-1 text-xs font-semibold text-[var(--bp-text-muted)]">把私聊交易变成可控履约</p>
    </div>
  </div>
);

const AudienceLine: React.FC<{ segment: AudienceSegment }> = ({ segment }) => {
  const Icon = segment.icon;

  return (
    <div className="grid grid-cols-[3.5rem_1fr] gap-4 border-t border-[var(--bp-border-soft)] py-5 first:border-t-0">
      <div className="flex h-11 w-11 items-center justify-center border border-[var(--bp-accent-border-strong)] bg-[var(--bp-tint)] text-sm font-black text-[var(--bp-accent)]">
        {segment.label}
      </div>
      <div>
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-[var(--bp-accent)]" />
          <h3 className="text-lg font-black leading-tight text-[var(--bp-text-strong)] sm:text-xl">
            {segment.title}
          </h3>
        </div>
        <p className="mt-2 max-w-[46rem] text-sm font-semibold leading-7 text-[var(--bp-text-muted)] sm:text-[15px]">
          {segment.body}
        </p>
      </div>
    </div>
  );
};

const CustomerPanel: React.FC<{
  eyebrow: string;
  title: string;
  segments: AudienceSegment[];
  icon: LucideIcon;
}> = ({ eyebrow, title, segments, icon: Icon }) => (
  <section className="border border-[var(--bp-border)] bg-[var(--bp-panel)] p-5 shadow-[0_18px_54px_var(--bp-shadow-soft)] sm:p-7">
    <div className="mb-4 flex items-end justify-between gap-4">
      <div>
        <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">{eyebrow}</p>
        <h2 className="mt-2 text-3xl font-black leading-none text-[var(--bp-text-strong)]">{title}</h2>
      </div>
      <Icon className="h-8 w-8 text-[var(--bp-accent)]" />
    </div>
    {segments.map((segment) => (
      <AudienceLine key={segment.label} segment={segment} />
    ))}
  </section>
);

const PainPanel: React.FC<{ column: PainColumn }> = ({ column }) => (
  <section className="border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-5 shadow-[0_18px_54px_var(--bp-shadow-soft)] sm:p-7">
    <div className="mb-6 flex items-start justify-between gap-4 border-b border-[var(--bp-border-soft)] pb-5">
      <div>
        <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-warning)]">{column.eyebrow}</p>
        <h3 className="mt-2 text-3xl font-black leading-tight text-[var(--bp-text)]">{column.title}</h3>
      </div>
      <AlertTriangle className="h-7 w-7 text-[var(--bp-warning)]" />
    </div>
    <p className="text-base font-black leading-7 text-[var(--bp-text-muted)]">{column.summary}</p>
    <div className="mt-6 grid gap-4">
      {column.points.map((point) => (
        <div key={point.label} className="grid grid-cols-[3.25rem_1fr] gap-4 border-t border-[var(--bp-border-soft)] pt-4">
          <div className="flex h-10 w-10 items-center justify-center border border-[var(--bp-warning-border)] bg-[var(--bp-warning-tint)] text-xs font-black text-[var(--bp-warning-strong)]">
            {point.label}
          </div>
          <div>
            <h4 className="text-lg font-black leading-tight text-[var(--bp-text)]">{point.title}</h4>
            <p className="mt-2 text-sm font-semibold leading-7 text-[var(--bp-text-muted)]">{point.body}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const SolutionCard: React.FC<{ step: SolutionStep }> = ({ step }) => {
  const Icon = step.icon;

  return (
    <section className="relative border border-[var(--bp-accent-border)] bg-[var(--bp-surface)] p-5 shadow-[0_18px_54px_var(--bp-shadow-soft)] sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">环节 {step.index}</p>
          <h3 className="mt-2 text-3xl font-black leading-none text-[var(--bp-text)]">{step.title}</h3>
        </div>
        <div className="flex h-12 w-12 items-center justify-center bg-[var(--bp-accent)] text-[var(--bp-on-accent)]">
          <Icon className="h-6 w-6" />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <div className="border border-[var(--bp-border)] bg-[var(--bp-surface-strong)] px-3 py-3 text-center text-sm font-black text-[var(--bp-text-muted)]">
          {step.from}
        </div>
        <ArrowRight className="h-4 w-4 text-[var(--bp-accent)]" />
        <div className="border border-[var(--bp-accent-border-strong)] bg-[var(--bp-tint)] px-3 py-3 text-center text-sm font-black text-[var(--bp-accent-text)]">
          {step.to}
        </div>
      </div>

      <p className="mt-6 text-sm font-semibold leading-7 text-[var(--bp-text-muted)]">{step.body}</p>
      <div className="mt-5 flex gap-3 border-t border-[var(--bp-border-soft)] pt-4">
        <ShieldCheck className="mt-1 h-5 w-5 shrink-0 text-[var(--bp-accent)]" />
        <p className="text-sm font-black leading-6 text-[var(--bp-text)]">{step.result}</p>
      </div>
    </section>
  );
};

const MiniWireframe: React.FC<{ page: ProductPage }> = ({ page }) => {
  const modules = page.wireframe.slice(1, 5);

  return (
    <div className="border border-[var(--bp-border-strong)] bg-[var(--bp-surface-strong)] p-3">
      <div className="grid gap-2">
        <div className="flex h-7 items-center justify-between border border-[var(--bp-border)] bg-[var(--bp-panel)] px-3">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 bg-[var(--bp-accent)]" />
            <span className="h-2 w-2 bg-[var(--bp-chart-1)]" />
            <span className="h-2 w-2 bg-[var(--bp-border)]" />
          </div>
          <span className="h-2 w-20 bg-[var(--bp-grid)]" />
        </div>

        <div className="grid gap-2 lg:grid-cols-[0.72fr_1fr]">
          <div className="grid gap-2">
            <div className="grid min-h-[82px] content-between bg-[var(--bp-accent)] p-3 text-[var(--bp-on-accent)]">
              <span className="text-xs font-black opacity-85">核心动作</span>
              <span className="text-2xl font-black leading-none">{page.wireframe[0]}</span>
              <div className="grid grid-cols-3 gap-1.5">
                {[0, 1, 2].map((item) => (
                  <span key={item} className="h-1.5 bg-[var(--bp-chart-1)]" />
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-2">
            {modules.map((item, index) => (
              <div
                key={item}
                className={`grid grid-cols-[4rem_1fr] items-center gap-3 border px-3 py-2.5 ${
                  index === 0
                    ? 'border-[var(--bp-grid-strong)] bg-[var(--bp-tint)]'
                    : 'border-[var(--bp-border)] bg-[var(--bp-panel)]'
                }`}
              >
                <span className="text-sm font-black leading-none text-[var(--bp-text)]">{item}</span>
                <div className="grid gap-1.5">
                  <span className="h-1.5 w-full bg-[var(--bp-grid-strong)]" />
                  <span className="h-1.5 w-2/3 bg-[var(--bp-border-soft)]" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductPagePanel: React.FC<{ page: ProductPage }> = ({ page }) => {
  const Icon = page.icon;

  return (
    <section className="grid gap-5 border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-5 shadow-[0_18px_48px_var(--bp-shadow-faint)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-black uppercase tracking-normal text-[var(--bp-accent)]">
            页面 {page.index} · {page.role}
          </p>
          <h3 className="mt-2 text-4xl font-black leading-none text-[var(--bp-text)]">{page.title}</h3>
          <p className="mt-3 text-lg font-black leading-tight text-[var(--bp-accent-muted)]">{page.subtitle}</p>
        </div>
        <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-[var(--bp-accent)] text-[var(--bp-on-accent)]">
          <Icon className="h-6 w-6" />
        </div>
      </div>

      <MiniWireframe page={page} />
    </section>
  );
};

const ProductJourneyFlow: React.FC = () => (
  <section className="grid gap-5">
    <div className="hidden grid-cols-[8rem_repeat(5,minmax(0,1fr))] gap-3 lg:grid">
      <div />
      {productFlowStages.map((stage) => (
        <div key={stage.label} className="border border-[var(--bp-accent-border)] bg-[var(--bp-tint)] px-3 py-3 text-center">
          <p className="text-sm font-black text-[var(--bp-text)]">{stage.page}</p>
          <p className="mt-1 text-xs font-black text-[var(--bp-accent)]">{stage.label}</p>
        </div>
      ))}
    </div>

    <div className="grid gap-3 lg:grid-cols-[8rem_repeat(5,minmax(0,1fr))]">
      <div className="hidden items-center justify-center border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-4 text-sm font-black text-[var(--bp-text-muted)] lg:flex">
        需求方
      </div>
      {productFlowStages.map((stage, index) => (
        <div key={`${stage.label}-buyer`} className="relative border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-4 shadow-[0_14px_34px_var(--bp-shadow-faint)]">
          {index < productFlowStages.length - 1 && (
            <div className="absolute -right-3 top-1/2 z-10 hidden h-px w-6 bg-[var(--bp-accent)] lg:block" />
          )}
          <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)] lg:hidden">{stage.page}</p>
          <h3 className="mt-1 text-xl font-black leading-tight text-[var(--bp-text)]">{stage.buyerAction}</h3>
          <p className="mt-2 text-sm font-semibold leading-6 text-[var(--bp-text-muted)]">{stage.caption}</p>
        </div>
      ))}
    </div>

    <div className="grid gap-3 lg:grid-cols-[8rem_repeat(5,minmax(0,1fr))]">
      <div className="hidden items-center justify-center border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-4 text-sm font-black text-[var(--bp-text-muted)] lg:flex">
        平台层
      </div>
      {productFlowStages.map((stage, index) => (
        <div key={`${stage.label}-platform`} className="relative border border-[var(--bp-grid-strong)] bg-[var(--bp-tint)] p-4">
          {index < productFlowStages.length - 1 && (
            <ArrowRight className="absolute -right-5 top-1/2 z-20 hidden h-7 w-7 -translate-y-1/2 bg-[var(--bp-tint)] p-1 text-[var(--bp-accent)] lg:block" />
          )}
          <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">步骤 {String(index + 1).padStart(2, '0')}</p>
          <h3 className="mt-3 text-2xl font-black leading-tight text-[var(--bp-text)]">{stage.label}</h3>
        </div>
      ))}
    </div>

    <div className="grid gap-3 lg:grid-cols-[8rem_repeat(5,minmax(0,1fr))]">
      <div className="hidden items-center justify-center border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-4 text-sm font-black text-[var(--bp-text-muted)] lg:flex">
        创作者
      </div>
      {productFlowStages.map((stage, index) => (
        <div key={`${stage.label}-creator`} className="relative border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-4 shadow-[0_14px_34px_var(--bp-shadow-faint)]">
          {index < productFlowStages.length - 1 && (
            <div className="absolute -right-3 top-1/2 z-10 hidden h-px w-6 bg-[var(--bp-accent)] lg:block" />
          )}
          <h3 className="text-xl font-black leading-tight text-[var(--bp-text)]">{stage.creatorAction}</h3>
          <p className="mt-2 text-sm font-semibold leading-6 text-[var(--bp-text-muted)]">{stage.caption}</p>
        </div>
      ))}
    </div>
  </section>
);

const MarketBubbleChart: React.FC = () => (
  <section className="border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-5 shadow-[0_18px_54px_var(--bp-shadow-soft)]">
    <div className="relative mx-auto aspect-square w-full max-w-[590px]">
      {marketLayers.map((layer, index) => (
        <div
          key={layer.label}
          className="absolute left-1/2 -translate-x-1/2 shadow-[inset_0_-18px_50px_var(--bp-shadow-diagram)]"
          style={{
            width: layer.size,
            height: layer.size,
            bottom: layer.bottom,
            backgroundColor: layer.color,
            borderRadius: '50%',
            zIndex: index + 1,
          }}
        />
      ))}
      <div className="pointer-events-none absolute inset-0 z-20 text-center font-black leading-none text-[var(--bp-on-accent)]">
        <p className="absolute left-1/2 top-[12%] -translate-x-1/2 whitespace-nowrap text-5xl sm:text-6xl">7,200亿</p>
        <p className="absolute left-1/2 top-[35%] -translate-x-1/2 whitespace-nowrap text-5xl sm:text-6xl">750亿</p>
        <p className="absolute left-1/2 top-[60%] -translate-x-1/2 whitespace-nowrap text-4xl sm:text-5xl">7.5亿</p>
        <p className="absolute left-1/2 top-[82%] -translate-x-1/2 whitespace-nowrap text-3xl sm:text-4xl">7500万</p>
      </div>
    </div>

    <div className="mt-5 grid gap-3 sm:grid-cols-2">
      {marketLayers.map((layer) => (
        <div key={layer.label} className="grid grid-cols-[1rem_1fr] gap-3 border-t border-[var(--bp-border-soft)] pt-3">
          <span className="mt-1 h-3 w-3" style={{ backgroundColor: layer.color }} />
          <div>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <p className="text-sm font-black text-[var(--bp-text)]">{layer.label}</p>
              <p className="text-sm font-black text-[var(--bp-accent)]">{layer.value}</p>
            </div>
            <p className="mt-1 text-xs font-semibold leading-5 text-[var(--bp-text-muted)]">{layer.caption}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const FinanceModelPanel: React.FC = () => (
  <section className="grid gap-4">
    <div className="border border-[var(--bp-accent-border)] bg-[var(--bp-tint)] p-5 shadow-[0_18px_44px_var(--bp-shadow-accent-soft)]">
      <div className="flex items-start justify-between gap-4">
        <div>
        <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">微观测算</p>
          <h3 className="mt-2 text-3xl font-black leading-tight text-[var(--bp-text)]">
            三年期财务测算
          </h3>
        </div>
        <WalletCards className="h-8 w-8 text-[var(--bp-accent)]" />
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {financeMetrics.map((metric) => (
          <div key={metric.label} className="border border-[var(--bp-accent-border)] bg-[var(--bp-surface)] p-4">
            <p className="text-sm font-black text-[var(--bp-accent-muted)]">{metric.label}</p>
            <p className="mt-2 text-4xl font-black leading-none text-[var(--bp-accent-strong)]">{metric.value}</p>
            <p className="mt-2 text-sm font-semibold leading-5 text-[var(--bp-text-muted)]">{metric.caption}</p>
          </div>
        ))}
      </div>
    </div>

    <div className="border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-5 shadow-[0_18px_54px_var(--bp-shadow-soft)]">
      <div className="flex items-center gap-3">
        <UsersRound className="h-7 w-7 text-[var(--bp-accent)]" />
        <p className="text-xl font-black text-[var(--bp-text)]">微观验证</p>
      </div>
      <div className="mt-5 grid gap-3">
        {[
          ['250万+', '2028 年中国预计 AIGC 自由创作者规模'],
          ['0.6%', '达成 7.5 亿 GMV，仅需渗透约 1.5 万名活跃创作者'],
          ['4,000元', '这 1.5 万人每月在平台接单赚取 4,000 元，即跑通模型'],
        ].map(([value, caption]) => (
          <div key={value} className="grid grid-cols-[7.5rem_1fr] items-center gap-4 border-t border-[var(--bp-border-soft)] pt-3 first:border-t-0 first:pt-0">
            <p className="text-3xl font-black leading-none text-[var(--bp-accent-strong)]">{value}</p>
            <p className="text-sm font-black leading-6 text-[var(--bp-text-muted)]">{caption}</p>
          </div>
        ))}
      </div>
      <p className="mt-5 border-t border-[var(--bp-border-soft)] pt-4 text-xs font-semibold leading-5 text-[var(--bp-text-subtle)]">
        推演依据：国家统计局 2 亿灵活就业大盘，智联招聘 1500 万泛数字内容创作者基数，按 2028 年 AIGC 在创意产业 15% 深度渗透率保守估算。
      </p>
    </div>
  </section>
);

const TrendSignalStrip: React.FC = () => (
  <section className="grid gap-3 lg:grid-cols-4">
    {trendSignals.map((signal) => (
      <div key={signal.label} className="border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-4 shadow-[0_14px_34px_var(--bp-shadow-faint)]">
        <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">{signal.label}</p>
        <p className="mt-2 text-3xl font-black leading-none text-[var(--bp-text)]">{signal.value}</p>
        <p className="mt-3 text-sm font-semibold leading-6 text-[var(--bp-text-muted)]">{signal.caption}</p>
      </div>
    ))}
  </section>
);

const RevenueModelOverview: React.FC = () => (
  <section className="border border-[var(--bp-accent-border)] bg-[var(--bp-tint)] p-5 shadow-[0_18px_44px_var(--bp-shadow-accent-soft)]">
    <div className="grid gap-5 lg:grid-cols-[18rem_1fr] lg:items-start">
      <div className="border border-[var(--bp-accent-border)] bg-[var(--bp-surface)] p-5">
        <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">收入目标</p>
        <p className="mt-2 text-5xl font-black leading-none text-[var(--bp-accent-strong)]">7500 万</p>
        <p className="mt-2 text-base font-black text-[var(--bp-text)]">年核心净营收</p>
        <div className="mt-5 border-t border-[var(--bp-border-soft)] pt-4">
          <p className="text-3xl font-black leading-none text-[var(--bp-text)]">625 万</p>
          <p className="mt-2 text-xs font-black text-[var(--bp-text-muted)]">月收入合计</p>
        </div>
      </div>
      <div>
        <div className="flex h-14 w-full border border-[var(--bp-grid-strong)] bg-[var(--bp-surface)]">
          {revenueStreams.map((stream) => (
            <div
              key={stream.type}
              className="h-full"
              style={{ width: stream.share, backgroundColor: stream.color }}
              title={`${stream.type} ${stream.share}`}
            />
          ))}
        </div>
        <div className="mt-4 grid gap-3 lg:grid-cols-4">
          {revenueStreams.map((stream) => (
            <div key={stream.type} className="border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 shrink-0" style={{ backgroundColor: stream.color }} />
                  <p className="text-sm font-black leading-tight text-[var(--bp-text)]">{stream.type}</p>
                </div>
                <p className="text-xl font-black leading-none text-[var(--bp-accent)]">{stream.share}</p>
              </div>
              <p className="mt-3 text-2xl font-black leading-none text-[var(--bp-text)]">{stream.annual}</p>
              <p className="mt-2 text-xs font-semibold leading-5 text-[var(--bp-text-muted)]">
                月收入 {stream.monthly}，{stream.assumption}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const OrderGmvEngine: React.FC = () => (
  <section className="border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-5 shadow-[0_18px_54px_var(--bp-shadow-soft)]">
    <div className="mb-5 flex items-end justify-between gap-4">
      <div>
        <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">订单引擎</p>
        <h3 className="mt-2 text-3xl font-black leading-tight text-[var(--bp-text)]">订单结构</h3>
      </div>
      <div className="text-right">
        <p className="text-3xl font-black leading-none text-[var(--bp-accent-strong)]">4687.5 万</p>
        <p className="mt-1 text-xs font-black text-[var(--bp-text-muted)]">月 GMV，约 27,000 单 / 月</p>
      </div>
    </div>

    <div className="grid gap-3">
      {orderMixRows.map((row) => (
        <div key={row.type} className="border border-[var(--bp-border-soft)] bg-[var(--bp-surface)] p-4">
          <div className="grid gap-3 lg:grid-cols-[1.25fr_0.7fr_0.7fr_0.7fr] lg:items-center">
            <div>
              <div className="flex items-center justify-between gap-3">
                <p className="text-lg font-black leading-tight text-[var(--bp-text)]">{row.type}</p>
                <p className="text-lg font-black text-[var(--bp-accent)]">{row.share}</p>
              </div>
              <div className="mt-3 h-3 bg-[var(--bp-bar)]">
                <div className="h-full" style={{ width: row.width, backgroundColor: row.color }} />
              </div>
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-text-subtle)]">平均客单价</p>
              <p className="mt-1 text-xl font-black text-[var(--bp-text)]">{row.avgPrice}</p>
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-text-subtle)]">月订单数</p>
              <p className="mt-1 text-xl font-black text-[var(--bp-text)]">{row.monthlyOrders}</p>
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-text-subtle)]">月 GMV</p>
              <p className="mt-1 text-xl font-black text-[var(--bp-text)]">{row.monthlyGmv}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const SubscriptionModelPanel: React.FC = () => (
  <section className="border border-[var(--bp-accent-border)] bg-[var(--bp-tint)] p-5 shadow-[0_18px_44px_var(--bp-shadow-accent-soft)]">
    <div>
      <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">订阅拆解</p>
      <h3 className="mt-1 text-3xl font-black leading-tight text-[var(--bp-text)]">订阅服务</h3>
      <p className="mt-2 text-sm font-black leading-6 text-[var(--bp-text-muted)]">
        只展示转化假设和订阅权益，收入已在上方收入结构统一体现。
      </p>
    </div>
      <div className="mt-5 grid gap-3">
        {subscriptionPlans.map((plan) => (
          <div key={plan.type} className="border border-[var(--bp-accent-border)] bg-[var(--bp-surface)] p-4">
            <p className="text-lg font-black leading-tight text-[var(--bp-text)]">{plan.type}</p>
            <div className="mt-4 grid grid-cols-2 gap-3 border-t border-[var(--bp-border-soft)] pt-3 sm:grid-cols-4">
              <div>
                <p className="text-xs font-black text-[var(--bp-text-subtle)]">用户基数</p>
                <p className="mt-1 text-base font-black leading-tight text-[var(--bp-text)]">{plan.userBase}</p>
              </div>
              <div>
                <p className="text-xs font-black text-[var(--bp-text-subtle)]">付费转化</p>
                <p className="mt-1 text-xl font-black text-[var(--bp-text)]">{plan.conversion}</p>
              </div>
              <div>
                <p className="text-xs font-black text-[var(--bp-text-subtle)]">月费</p>
                <p className="mt-1 text-xl font-black text-[var(--bp-text)]">{plan.monthlyFee}</p>
              </div>
              <div>
                <p className="text-xs font-black text-[var(--bp-text-subtle)]">付费用户</p>
                <p className="mt-1 text-xl font-black text-[var(--bp-text)]">{plan.paidUsers}</p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {plan.features.map((feature) => (
                <span key={feature} className="border border-[var(--bp-border-strong)] bg-[var(--bp-accent-softer)] px-3 py-2 text-xs font-black leading-none text-[var(--bp-text)]">
                  {feature}
                </span>
              ))}
            </div>
          </div>
        ))}
    </div>
  </section>
);

const CostBreakdownPanel: React.FC = () => (
  <section className="border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-5 shadow-[0_18px_54px_var(--bp-shadow-soft)]">
    <div className="mb-5 flex items-end justify-between gap-4">
      <div>
        <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">成本底座</p>
        <h3 className="mt-2 text-3xl font-black leading-tight text-[var(--bp-text)]">成本与费用</h3>
      </div>
      <div className="text-right">
        <p className="text-4xl font-black leading-none text-[var(--bp-accent-strong)]">1,500 万</p>
        <p className="mt-1 text-xs font-black text-[var(--bp-text-muted)]">年度合计</p>
      </div>
    </div>

    <div className="grid gap-3">
      {costItems.map((item) => (
        <div key={item.item} className="grid gap-3 border-t border-[var(--bp-border-soft)] pt-3 first:border-t-0 first:pt-0 sm:grid-cols-[1fr_7rem] sm:items-center">
          <div>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-base font-black leading-tight text-[var(--bp-text)]">{item.item}</p>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">{item.group}</p>
            </div>
            <div className="mt-2 h-2 bg-[var(--bp-bar)]">
              <div className="h-full bg-[var(--bp-accent)]" style={{ width: item.width }} />
            </div>
          </div>
          <p className="text-right text-xl font-black leading-none text-[var(--bp-text)]">{item.cost}</p>
        </div>
      ))}
    </div>
  </section>
);

const ProfitEstimatePanel: React.FC = () => (
  <section className="grid gap-5">
    <div className="border border-[var(--bp-accent-border)] bg-[var(--bp-tint)] p-5 shadow-[0_18px_44px_var(--bp-shadow-accent-soft)]">
      <div className="grid gap-4 lg:grid-cols-[1fr_auto_1fr_auto_1fr] lg:items-center">
        <div className="border border-[var(--bp-accent-border)] bg-[var(--bp-surface)] p-5 text-center">
          <p className="text-sm font-black text-[var(--bp-accent-muted)]">平台收入</p>
          <p className="mt-2 text-5xl font-black leading-none text-[var(--bp-accent-strong)]">7,500 万</p>
        </div>
        <span className="hidden text-3xl font-black text-[var(--bp-accent)] lg:block">−</span>
        <div className="border border-[var(--bp-border)] bg-[var(--bp-surface)] p-5 text-center">
          <p className="text-sm font-black text-[var(--bp-accent-muted)]">成本与费用</p>
          <p className="mt-2 text-5xl font-black leading-none text-[var(--bp-text)]">1,500 万</p>
        </div>
        <span className="hidden text-3xl font-black text-[var(--bp-accent)] lg:block">=</span>
        <div className="bg-[var(--bp-accent)] p-5 text-center text-[var(--bp-on-accent)] shadow-[0_18px_44px_var(--bp-shadow-accent)]">
          <p className="text-sm font-black">净利润</p>
          <p className="mt-2 text-5xl font-black leading-none">5,000 万</p>
        </div>
      </div>
    </div>

    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {profitMetrics.map((metric) => (
        <div key={metric.label} className="border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-4 shadow-[0_14px_34px_var(--bp-shadow-faint)]">
          <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">{metric.label}</p>
          <p className="mt-2 text-3xl font-black leading-none text-[var(--bp-text)]">{metric.value}</p>
          <p className="mt-2 text-sm font-semibold leading-6 text-[var(--bp-text-muted)]">{metric.caption}</p>
        </div>
      ))}
    </div>
  </section>
);

const CompetitiveMatrix: React.FC = () => {
  const competitorProfiles = [
    ['云作', 'AIGC 垂直专业工作站', '本土合规、原生交付、双端订阅'],
    ['海外巨头', 'Upwork / Fiverr', '机制成熟，本土适配弱'],
    ['传统威客', '猪八戒 / 一品威客', '供给大，体验和品牌老化'],
    ['垂直标杆', '米画师', '体验强，赛道边界窄'],
  ];
  const groups = ['平台功能', '市场生态', '商业模式'];

  return (
    <section className="grid gap-4">
      <div className="grid gap-3 lg:grid-cols-[1.25fr_repeat(3,1fr)]">
        {competitorProfiles.map(([name, position, note], index) => (
          <div
            key={name}
            className={`border p-4 shadow-[0_14px_34px_var(--bp-shadow-faint)] ${
              index === 0
                ? 'border-[var(--bp-accent-border-strong)] bg-[var(--bp-accent)] text-[var(--bp-on-accent)] shadow-[0_18px_44px_var(--bp-shadow-accent)]'
                : 'border-[var(--bp-border-strong)] bg-[var(--bp-panel)]'
            }`}
          >
            <p
              className={`text-xs font-black uppercase tracking-normal ${
                index === 0 ? 'text-[var(--bp-on-accent-soft)]' : 'text-[var(--bp-accent)]'
              }`}
            >
              {String(index + 1).padStart(2, '0')}
            </p>
            <p className={`mt-2 font-black leading-tight ${index === 0 ? 'text-3xl' : 'text-2xl text-[var(--bp-text)]'}`}>
              {name}
            </p>
            <p className={`mt-2 text-sm font-black leading-6 ${index === 0 ? 'text-[var(--bp-on-accent-soft)]' : 'text-[var(--bp-accent-muted)]'}`}>
              {position}
            </p>
            <p className={`mt-2 text-xs font-semibold leading-5 ${index === 0 ? 'text-[var(--bp-border-soft)]' : 'text-[var(--bp-text-muted)]'}`}>
              {note}
            </p>
          </div>
        ))}
      </div>

      <div className="grid gap-3">
        {groups.map((group) => (
          <div key={group} className="overflow-x-auto border border-[var(--bp-border-strong)] bg-[var(--bp-surface)] shadow-[0_14px_34px_var(--bp-shadow-faint)]">
            <div className="min-w-[1120px]">
              <div className="grid grid-cols-[8rem_1.35fr_repeat(3,1fr)] bg-[var(--bp-tint)] text-xs font-black uppercase tracking-normal text-[var(--bp-accent-muted)]">
                <div className="border-r border-[var(--bp-accent-border)] px-4 py-3 text-[var(--bp-accent)]">
                  {group}
                </div>
                {['云作胜点', '海外巨头', '传统威客', '垂直标杆'].map((heading) => (
                  <div key={heading} className="border-r border-[var(--bp-accent-border)] px-4 py-3 last:border-r-0">
                    {heading}
                  </div>
                ))}
              </div>
              {competitionRows
                .filter((row) => row.category === group)
                .map((row) => (
                  <div
                    key={`${row.category}-${row.metric}`}
                    className="grid grid-cols-[8rem_1.35fr_repeat(3,1fr)] border-t border-[var(--bp-border)] text-[13px] font-semibold leading-6 text-[var(--bp-text-muted)]"
                  >
                    <div className="border-r border-[var(--bp-border-soft)] bg-[var(--bp-frame)] px-4 py-3 font-black text-[var(--bp-text)]">
                      {row.metric}
                    </div>
                    <div className="border-r border-[var(--bp-accent-border)] bg-[var(--bp-tint)] px-4 py-3 font-black text-[var(--bp-text)]">
                      {row.cloud}
                    </div>
                    <div className="border-r border-[var(--bp-border-soft)] px-4 py-3">{row.overseas}</div>
                    <div className="border-r border-[var(--bp-border-soft)] px-4 py-3">{row.traditional}</div>
                    <div className="px-4 py-3">{row.vertical}</div>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const CompetitiveConclusionPanel: React.FC = () => {
  const competitorNames = ['Upwork / Fiverr', '猪八戒 / 一品威客', '米画师'];
  const icons = [ShieldCheck, Sparkles, Layers3];

  return (
    <section className="grid gap-4">
      {competitiveConclusions.map((item, index) => {
        const Icon = icons[index];

        return (
          <article
            key={item.target}
            className="grid gap-4 border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-4 shadow-[0_14px_34px_var(--bp-shadow-faint)] lg:grid-cols-[18rem_1fr_17rem] lg:items-center"
          >
            <div className="border border-[var(--bp-accent-border)] bg-[var(--bp-tint)] p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">
                  对比对象
                </p>
                <Icon className="h-5 w-5 text-[var(--bp-accent)]" />
              </div>
              <p className="mt-3 text-4xl font-black leading-none text-[var(--bp-text)]">
                {competitorNames[index]}
              </p>
              <p className="mt-3 text-sm font-black leading-6 text-[var(--bp-accent-muted)]">{item.target}</p>
            </div>

            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">云作胜点</p>
              <h3 className="mt-2 text-3xl font-black leading-tight text-[var(--bp-text)]">{item.win}</h3>
              <p className="mt-3 max-w-[62rem] text-sm font-semibold leading-7 text-[var(--bp-text-muted)]">
                {item.body}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 lg:justify-end">
              {item.proofs.map((proof) => (
                <span
                  key={proof}
                  className="border border-[var(--bp-accent-border)] bg-[var(--bp-surface)] px-3 py-2 text-xs font-black leading-none text-[var(--bp-text)]"
                >
                  {proof}
                </span>
              ))}
            </div>
          </article>
        );
      })}

      <div className="grid gap-4 border border-[var(--bp-accent-border)] bg-[var(--bp-accent)] p-5 text-[var(--bp-on-accent)] shadow-[0_18px_44px_var(--bp-shadow-accent)] lg:grid-cols-[18rem_1fr] lg:items-center">
        <div>
          <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-border-soft)]">核心判断</p>
          <p className="mt-2 text-3xl font-black leading-tight">云作的定位不是替代某一家平台</p>
        </div>
        <p className="text-xl font-black leading-8">
          而是把海外平台的交易闭环、传统威客的供需规模、垂直社区的专业粘性，重新组合成 AIGC 原生外包基础设施。
        </p>
      </div>
    </section>
  );
};

const LaunchStrategyPanel: React.FC = () => (
  <section className="grid gap-5">
    <div className="grid gap-4 border border-[var(--bp-accent-border)] bg-[var(--bp-tint)] p-5 shadow-[0_18px_44px_var(--bp-shadow-accent-soft)] lg:grid-cols-[20rem_1fr] lg:items-center">
      <div>
        <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">Cold Start Flywheel</p>
        <p className="mt-2 text-4xl font-black leading-tight text-[var(--bp-text)]">三阶段冷启动飞轮</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr] sm:items-center">
        {launchPhases.map((phase, index) => (
          <React.Fragment key={phase.index}>
            <div className="border border-[var(--bp-accent-border)] bg-[var(--bp-surface)] px-4 py-4 text-center">
              <p className="text-sm font-black text-[var(--bp-accent)]">{phase.duration}</p>
              <p className="mt-2 text-3xl font-black leading-none text-[var(--bp-text)]">{phase.highlight}</p>
              <p className="mt-2 text-xs font-black text-[var(--bp-text-muted)]">{phase.goal}</p>
            </div>
            {index < launchPhases.length - 1 && (
              <ArrowRight className="mx-auto hidden h-6 w-6 text-[var(--bp-accent)] sm:block" />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>

    <div className="grid gap-4 lg:grid-cols-3">
      {launchPhases.map((phase, index) => (
        <article
          key={phase.index}
          className={`grid content-between gap-5 border p-5 shadow-[0_14px_34px_var(--bp-shadow-faint)] ${
            index === 0
              ? 'border-[var(--bp-accent-border-strong)] bg-[var(--bp-panel)]'
              : 'border-[var(--bp-border-strong)] bg-[var(--bp-panel)]'
          }`}
        >
          <div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">
                  Phase {phase.index} · {phase.analogy}
                </p>
                <h3 className="mt-2 text-3xl font-black leading-tight text-[var(--bp-text)]">{phase.phase}</h3>
              </div>
              <div className="bg-[var(--bp-accent)] px-3 py-2 text-right text-[var(--bp-on-accent)]">
                <p className="text-xs font-black">{phase.duration}</p>
                <p className="text-lg font-black leading-none">{phase.highlight}</p>
              </div>
            </div>

            <p className="mt-4 text-sm font-black leading-7 text-[var(--bp-text-muted)]">{phase.narrative}</p>

            <div className="mt-5 border-t border-[var(--bp-border-soft)] pt-4">
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">关键动作</p>
              <div className="mt-3 grid gap-2">
                {phase.actions.map((action) => (
                  <div key={action} className="grid grid-cols-[1.25rem_1fr] gap-2">
                    <Check className="mt-1 h-4 w-4 text-[var(--bp-accent)]" />
                    <p className="text-sm font-semibold leading-6 text-[var(--bp-text-muted)]">{action}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">获客 / 招募渠道</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {phase.channels.map((channel) => (
                  <span
                    key={channel}
                    className="border border-[var(--bp-border-strong)] bg-[var(--bp-accent-softer)] px-3 py-2 text-xs font-black leading-none text-[var(--bp-text)]"
                  >
                    {channel}
                  </span>
                ))}
              </div>
            </div>

            <div className="border border-[var(--bp-accent-border)] bg-[var(--bp-tint)] p-4">
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">阶段结果</p>
              <p className="mt-2 text-sm font-black leading-6 text-[var(--bp-text)]">{phase.result}</p>
            </div>
          </div>
        </article>
      ))}
    </div>

    <div className="grid gap-4 border border-[var(--bp-accent-border)] bg-[var(--bp-accent)] p-5 text-[var(--bp-on-accent)] shadow-[0_18px_44px_var(--bp-shadow-accent)] lg:grid-cols-[18rem_1fr] lg:items-center">
      <div>
        <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-border-soft)]">飞轮逻辑</p>
        <p className="mt-2 text-3xl font-black leading-tight">供给密度先行</p>
      </div>
      <p className="text-xl font-black leading-8">
        先用强运营跑通 10 个真实订单，再沉淀 100 位稳定创作者。供给、案例、价格和履约保障形成优势后，需求增长反过来驱动创作者持续入驻。
      </p>
    </div>
  </section>
);

const FounderPhoto: React.FC<{ founder: FounderProfile }> = ({ founder }) => (
  <div className="relative mx-auto h-80 w-80 overflow-hidden border border-[var(--bp-accent-border-strong)] bg-[var(--bp-tint)] p-3 shadow-[0_22px_54px_var(--bp-shadow-accent)]" style={{ borderRadius: '50%' }}>
    <div className="h-full w-full overflow-hidden border border-[var(--bp-border-soft)] bg-[var(--bp-surface)]" style={{ borderRadius: '50%' }}>
      <img
        src={founder.image}
        alt={founder.title}
        className="h-full w-full object-cover"
        style={{ objectPosition: founder.imagePosition }}
      />
    </div>
  </div>
);

const FounderCard: React.FC<{ founder: FounderProfile }> = ({ founder }) => (
  <article className="grid grid-rows-[20rem_auto_13.5rem_auto] gap-5 border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-6 text-center shadow-[0_14px_34px_var(--bp-shadow-faint)]">
    <FounderPhoto founder={founder} />

    <h3 className="text-3xl font-black leading-tight text-[var(--bp-text)]">{founder.title}</h3>

    <div className="mx-auto grid max-w-[24rem] content-start gap-3 text-left">
      {founder.lines.map((line) => (
        <p key={line} className="text-base font-semibold leading-7 text-[var(--bp-text-muted)]">
          {line}
        </p>
      ))}
    </div>

    <div className="border border-[var(--bp-accent-border-strong)] bg-[var(--bp-tint)] px-4 py-4 text-left">
      <p className="text-lg font-black leading-7 text-[var(--bp-text)]">{founder.responsibility}</p>
    </div>
  </article>
);

const FoundingTeamPanel: React.FC = () => (
  <section>
    <div className="grid gap-5 lg:grid-cols-3">
      {founders.map((founder) => (
        <FounderCard key={founder.title} founder={founder} />
      ))}
    </div>
  </section>
);

const MilestoneMarkerPin: React.FC<{ marker: MilestoneMarker }> = ({ marker }) => {
  const milestone = milestones[marker.milestoneIndex];
  const isFunding = milestone.variant === 'funding';
  const isProduct = milestone.variant === 'product';
  const markerColor = isFunding
    ? 'var(--bp-accent-strong)'
    : isProduct
      ? 'var(--bp-chart-2)'
      : 'var(--bp-accent)';

  return (
    <>
      <div
        className="absolute z-20 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center border-4 border-[var(--bp-frame)] text-sm font-black text-[var(--bp-on-accent)] shadow-[0_8px_22px_var(--bp-shadow-pin)]"
        style={{ left: marker.x, top: marker.y, backgroundColor: markerColor, borderRadius: '50%' }}
      >
        {String(marker.milestoneIndex + 1).padStart(2, '0')}
      </div>
      <article
        className={`absolute z-30 border bg-[var(--bp-panel)] px-4 py-3 shadow-[0_8px_24px_var(--bp-shadow-soft)] ${
          isFunding ? 'border-[var(--bp-chart-2)]' : 'border-[var(--bp-border-strong)]'
        }`}
        style={{ left: marker.labelX, top: marker.labelY, width: marker.width }}
      >
        <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">{milestone.time}</p>
        <h3 className="mt-1 text-base font-black leading-tight text-[var(--bp-text)]">{milestone.title}</h3>
        <p className="mt-1 text-xs font-bold leading-5 text-[var(--bp-text-muted)]">{milestone.finance}</p>
      </article>
    </>
  );
};

const MilestoneTimelinePanel: React.FC = () => {
  return (
    <section>
      <div className="relative min-h-[650px] overflow-visible border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-5 shadow-[0_18px_54px_var(--bp-shadow-soft)]">
        <svg className="absolute inset-0 z-0 h-full w-full overflow-visible" viewBox="0 0 1200 640" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="milestonePathBlue" x1="0%" x2="100%" y1="0%" y2="100%">
              <stop offset="0%" stopColor="var(--bp-path-1)" />
              <stop offset="50%" stopColor="var(--bp-path-2)" />
              <stop offset="100%" stopColor="var(--bp-path-3)" />
            </linearGradient>
            <filter id="milestonePathShadow" x="-4%" y="-8%" width="108%" height="116%">
              <feDropShadow dx="0" dy="16" stdDeviation="18" floodColor="var(--bp-path-shadow)" floodOpacity="0.18" />
            </filter>
          </defs>
          <path
            d="M 96 174 H 1028 C 1146 174 1146 326 1028 326 H 172 C 56 326 56 540 172 540 H 1060"
            fill="none"
            stroke="var(--bp-path-soft)"
            strokeWidth="46"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.34"
          />
          <path
            d="M 1010 486 L 1164 540 L 1010 594 Z"
            fill="var(--bp-path-soft)"
            opacity="0.34"
          />
          <path
            d="M 96 174 H 1028 C 1146 174 1146 326 1028 326 H 172 C 56 326 56 540 172 540 H 1068"
            fill="none"
            stroke="url(#milestonePathBlue)"
            strokeWidth="12"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#milestonePathShadow)"
          />
          <path
            d="M 1034 502 L 1148 540 L 1034 578 Z"
            fill="var(--bp-path-3)"
            filter="url(#milestonePathShadow)"
          />
          <path
            d="M 96 174 H 1028 C 1146 174 1146 326 1028 326 H 172 C 56 326 56 540 172 540 H 1030"
            fill="none"
            stroke="var(--bp-path-highlight)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.55"
          />
        </svg>

        <div className="hidden lg:block">
          {milestoneMarkers.map((marker) => (
            <MilestoneMarkerPin key={marker.milestoneIndex} marker={marker} />
          ))}
        </div>

        <div className="relative z-10 mt-20 grid gap-3 lg:hidden">
          {milestones.map((milestone, index) => (
            <div key={`${milestone.time}-${milestone.title}`} className="grid grid-cols-[2.25rem_1fr] gap-3 border-t border-[var(--bp-border)] pt-3 first:border-t-0 first:pt-0">
              <span
                className="flex h-8 w-8 items-center justify-center bg-[var(--bp-accent)] text-xs font-black text-[var(--bp-on-accent)]"
                style={{ borderRadius: '50%' }}
              >
                {String(index + 1).padStart(2, '0')}
              </span>
              <div>
                <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">{milestone.time}</p>
                <p className="text-base font-black leading-tight text-[var(--bp-text)]">{milestone.title}</p>
                <p className="mt-1 text-xs font-semibold leading-5 text-[var(--bp-text-muted)]">{milestone.finance}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FundingRoundPanel: React.FC<{ round: FundingRound; index: number }> = ({ round, index }) => (
  <article className={`${index === 0 ? 'bg-[var(--bp-tint)]' : 'bg-[var(--bp-panel)]'} border border-[var(--bp-accent-border)] p-6 shadow-[0_18px_54px_var(--bp-shadow-soft)]`}>
    <div className="flex items-start justify-between gap-5">
      <div>
        <p className="text-sm font-black uppercase tracking-normal text-[var(--bp-accent)]">
          轮次 {String(index + 1).padStart(2, '0')}
        </p>
        <h3 className="mt-2 text-4xl font-black leading-none text-[var(--bp-text)]">{round.name}</h3>
      </div>
      <div className="text-right">
        <p className="text-5xl font-black leading-none text-[var(--bp-accent-strong)]">{round.amount}</p>
        <p className="mt-2 text-xs font-black text-[var(--bp-text-muted)]">融资金额</p>
      </div>
    </div>

    <div className="mt-6 grid grid-cols-2 gap-3 border-t border-[var(--bp-accent-border)] pt-5">
      <div>
        <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-text-subtle)]">出让股权</p>
        <p className="mt-1 text-3xl font-black leading-none text-[var(--bp-text)]">{round.equity}</p>
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-text-subtle)]">投后估值</p>
        <p className="mt-1 text-3xl font-black leading-none text-[var(--bp-text)]">{round.valuation}</p>
      </div>
    </div>

    <div className="mt-6 border border-[var(--bp-accent-border)] bg-[var(--bp-surface)] p-4">
      <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">阶段目标</p>
      <p className="mt-2 text-lg font-black leading-7 text-[var(--bp-text)]">{round.goal}</p>
    </div>
  </article>
);

const FundUsePanel: React.FC<{ title: string; total: number; uses: FundUse[] }> = ({ title, total, uses }) => (
  <section className="border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] p-5 shadow-[0_14px_34px_var(--bp-shadow-faint)]">
    <div className="mb-5 flex items-end justify-between gap-4">
      <div>
        <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">资金用途</p>
        <h3 className="mt-1 text-2xl font-black leading-tight text-[var(--bp-text)]">{title}</h3>
      </div>
      <div className="text-right">
        <p className="text-3xl font-black leading-none text-[var(--bp-accent-strong)]">{total} 万</p>
        <p className="mt-1 text-xs font-black text-[var(--bp-text-muted)]">合计</p>
      </div>
    </div>

    <div className="grid gap-3">
      {uses.map((item) => (
        <div key={item.label} className="grid gap-2">
          <div className="flex items-baseline justify-between gap-4">
            <p className="text-sm font-black leading-tight text-[var(--bp-text)]">{item.label}</p>
            <p className="text-sm font-black text-[var(--bp-accent)]">{item.amount} 万</p>
          </div>
          <div className="h-2.5 bg-[var(--bp-bar)]">
            <div
              className="h-full bg-[var(--bp-accent)]"
              style={{ width: `${(item.amount / total) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  </section>
);

const FundingTargetPanel: React.FC = () => (
  <section className="grid gap-5">
    <div className="grid gap-4 lg:grid-cols-[1fr_4rem_1fr] lg:items-center">
      <FundingRoundPanel round={fundingRounds[0]} index={0} />
      <div className="hidden justify-center lg:flex">
        <div className="flex h-14 w-14 items-center justify-center border border-[var(--bp-accent-border)] bg-[var(--bp-tint)] text-[var(--bp-accent)]">
          <ArrowRight className="h-7 w-7" />
        </div>
      </div>
      <FundingRoundPanel round={fundingRounds[1]} index={1} />
    </div>

    <div className="grid gap-5 lg:grid-cols-2">
      <FundUsePanel title="天使轮资金用途" total={60} uses={angelFundUses} />
      <FundUsePanel title="种子轮资金用途" total={300} uses={seedFundUses} />
    </div>
  </section>
);

const BusinessPlan: React.FC = () => {
  return (
    <main
      className="bp-plan min-h-screen overflow-x-hidden bg-[var(--bp-page-bg)] text-[var(--bp-text-strong)] [letter-spacing:0] [&_h1]:[font-family:var(--bp-display-font)] [&_h2]:[font-family:var(--bp-display-font)] [&_h3]:[font-family:var(--bp-display-font)]"
      data-bp-variant="bp1"
      style={businessPlanTheme}
    >
      <SlideFrame id="cover">
        <div className="relative z-10 grid w-full gap-10 px-7 sm:px-10 lg:grid-cols-[0.95fr_0.8fr] lg:items-center lg:px-16">
          <div className="flex min-h-[540px] flex-col justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center bg-[var(--bp-accent)] text-[var(--bp-on-accent)]">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--bp-text-muted)]">CraftCloud 云作 · 商业计划书</p>
              </div>
            </div>

            <div>
              <h1 className="max-w-4xl text-5xl font-black leading-[0.94] text-[var(--bp-accent-strong)] sm:text-7xl lg:text-[112px]">
                AIGC
                <br />
                外包平台
              </h1>
              <p className="mt-8 max-w-3xl text-2xl font-black leading-tight text-[var(--bp-text)] sm:text-4xl lg:text-5xl">
                找对人，保交付，稳支付。
              </p>
              <p className="mt-7 max-w-2xl text-lg font-semibold leading-8 text-[var(--bp-text-muted)]">
                面向中国 AIGC 创作交易的在线履约层，把找人、沟通、合同、里程碑和资金托管放进同一个闭环。
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 border border-[var(--bp-border-strong)] bg-[var(--bp-panel)] px-5 py-3 text-sm font-black text-[var(--bp-text-muted)]">
                <Layers3 className="h-4 w-4 text-[var(--bp-accent)]" />
                撮合 · 履约 · 支付
              </span>
            </div>
          </div>

          <div className="hidden justify-center md:flex lg:justify-end">
            <CoverDiagram />
          </div>
        </div>
      </SlideFrame>

      <SlideFrame id="customers">
        <div className="relative z-10 grid w-full gap-7 px-6 sm:px-10 lg:px-14">
          <header className="grid gap-5 lg:grid-cols-[0.55fr_1fr] lg:items-end">
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">
                第 01 页 · 目标客户
              </p>
              <h2 className="mt-3 text-5xl font-black leading-none text-[var(--bp-accent-strong)] sm:text-6xl">
                目标客户
              </h2>
            </div>
            <p className="max-w-5xl text-2xl font-black leading-tight text-[var(--bp-text)] sm:text-3xl">
              云作连接 AIGC 创作交易中的两端用户：需要创意交付的需求方，以及具备 AI 生产能力的创作者。
            </p>
          </header>

          <div className="grid gap-5 lg:grid-cols-[1fr_10rem_1fr] lg:items-center">
            <CustomerPanel eyebrow="需求方" title="需求方" icon={Palette} segments={buyerSegments} />

            <div className="flex items-center justify-center">
              <div className="grid w-full gap-3 border border-[var(--bp-accent-border)] bg-[var(--bp-tint-strong)] p-4 text-center shadow-[0_18px_44px_var(--bp-shadow-accent-soft)]">
                <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">云作交易层</p>
                <p className="text-xl font-black leading-tight text-[var(--bp-text)]">云作交易层</p>
                <div className="mx-auto hidden h-16 w-px bg-[var(--bp-grid-strong)] lg:block" />
                <div className="flex items-center justify-center gap-2 text-sm font-black text-[var(--bp-accent-muted)] lg:grid">
                  <span>撮合</span>
                  <ArrowRight className="h-4 w-4 lg:rotate-90" />
                  <span>履约</span>
                  <ArrowRight className="h-4 w-4 lg:rotate-90" />
                  <span>收付款</span>
                </div>
              </div>
            </div>

            <CustomerPanel eyebrow="创作者" title="创作者" icon={Sparkles} segments={creatorSegments} />
          </div>
        </div>
      </SlideFrame>

      <SlideFrame id="problem">
        <div className="relative z-10 grid w-full gap-8 px-6 sm:px-10 lg:px-14">
          <header className="grid gap-5 lg:grid-cols-[0.58fr_1fr] lg:items-end">
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-warning)]">
                第 02 页 · 市场痛点
              </p>
              <h2 className="mt-3 text-5xl font-black leading-none text-[var(--bp-accent-strong)] sm:text-6xl">
                痛点
              </h2>
            </div>
            <p className="max-w-5xl text-2xl font-black leading-tight text-[var(--bp-text)] sm:text-3xl">
              AIGC 外包市场不是缺需求，也不是缺创作者，而是缺专业交易基础设施。
            </p>
          </header>

          <div className="grid gap-5 lg:grid-cols-[1fr_16rem_1fr] lg:items-center">
            <PainPanel column={painColumns[0]} />

            <div className="border border-[var(--bp-warning-border)] bg-[var(--bp-warning-soft)] p-5 text-center shadow-[0_18px_44px_var(--bp-shadow-warm)]">
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-warning-strong)]">核心摩擦</p>
              <p className="mt-4 text-3xl font-black leading-tight text-[var(--bp-text)]">
                找不到对方
                <br />
                信不了对方
              </p>
              <div className="mx-auto my-5 h-px w-20 bg-[var(--bp-warning-border)]" />
              <p className="text-sm font-black leading-7 text-[var(--bp-text-muted)]">
                信息、沟通、履约、支付全部散落在私域里，交易摩擦被双方共同承担。
              </p>
            </div>

            <PainPanel column={painColumns[1]} />
          </div>

        </div>
      </SlideFrame>

      <SlideFrame id="solution">
        <div className="relative z-10 grid w-full gap-8 px-6 sm:px-10 lg:px-14">
          <header className="grid gap-5 lg:grid-cols-[0.58fr_1fr] lg:items-end">
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">
                第 03 页 · 解决方案
              </p>
              <h2 className="mt-3 text-5xl font-black leading-none text-[var(--bp-accent-strong)] sm:text-6xl">
                解决方案
              </h2>
            </div>
            <p className="max-w-5xl text-2xl font-black leading-tight text-[var(--bp-text)] sm:text-3xl">
              云作围绕 AI 创作交易最关键的三个环节，提供标准化交易保障。
            </p>
          </header>

          <div className="grid gap-5 lg:grid-cols-3">
            {solutionSteps.map((step) => (
              <SolutionCard key={step.index} step={step} />
            ))}
          </div>

          <div className="grid gap-5 border border-[var(--bp-accent-border)] bg-[var(--bp-tint)] p-5 shadow-[0_18px_44px_var(--bp-shadow-accent-soft)] lg:grid-cols-[16rem_1fr] lg:items-center">
            <div className="flex items-center gap-3">
              <MessageSquareText className="h-7 w-7 text-[var(--bp-accent)]" />
              <p className="text-xl font-black text-[var(--bp-text)]">从私域交易到平台履约</p>
            </div>
            <p className="text-base font-black leading-7 text-[var(--bp-text-muted)]">
              自媒体平台和微信群只能让双方互相看见。云作要做的是让双方在沟通前可筛选，在项目中可追踪，在付款时有托管，在争议发生时有规则。
            </p>
          </div>

        </div>
      </SlideFrame>

      <SlideFrame id="product">
        <div className="relative z-10 grid w-full gap-7 px-6 sm:px-10 lg:px-14">
          <header className="grid gap-5 lg:grid-cols-[0.52fr_1fr] lg:items-end">
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">
                第 04 页 · 产品形态
              </p>
              <h2 className="mt-3 text-5xl font-black leading-none text-[var(--bp-accent-strong)] sm:text-6xl">
                产品形态
              </h2>
            </div>
            <p className="max-w-5xl text-2xl font-black leading-tight text-[var(--bp-text)] sm:text-3xl">
              响应式网页平台，围绕发现创作者、发布需求、沟通方案、签约交付、托管支付构建完整交易流程。
            </p>
          </header>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {productPages.map((page) => (
              <ProductPagePanel key={page.index} page={page} />
            ))}
          </div>

        </div>
      </SlideFrame>

      <SlideFrame id="product-flow">
        <div className="relative z-10 grid w-full gap-8 px-6 sm:px-10 lg:px-14">
          <header className="grid gap-5 lg:grid-cols-[0.52fr_1fr] lg:items-end">
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">
                第 05 页 · 核心路径
              </p>
              <h2 className="mt-3 text-5xl font-black leading-none text-[var(--bp-accent-strong)] sm:text-6xl">
                核心路径
              </h2>
            </div>
            <p className="max-w-5xl text-2xl font-black leading-tight text-[var(--bp-text)] sm:text-3xl">
              从看到作品与需求，到方案确认、试稿验证、签约履约、阶段付款，用户始终留在同一个交易上下文里。
            </p>
          </header>

          <ProductJourneyFlow />

          <div className="grid gap-5 border border-[var(--bp-accent-border)] bg-[var(--bp-tint)] p-5 shadow-[0_18px_44px_var(--bp-shadow-accent-soft)] lg:grid-cols-[18rem_1fr] lg:items-center">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-7 w-7 text-[var(--bp-accent)]" />
              <p className="text-xl font-black text-[var(--bp-text)]">闭环价值</p>
            </div>
            <p className="text-base font-black leading-7 text-[var(--bp-text-muted)]">
              找人靠公开作品与结构化筛选，推进靠聊天里的交易动作，风险靠试稿和合同拆小，收付款靠托管和里程碑释放。
            </p>
          </div>

        </div>
      </SlideFrame>

      <SlideFrame id="market-size">
        <div className="relative z-10 grid w-full gap-7 px-6 sm:px-10 lg:px-14">
          <header className="grid gap-5 lg:grid-cols-[0.48fr_1fr] lg:items-end">
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">
                第 06 页 · 市场规模
              </p>
              <h2 className="mt-3 text-5xl font-black leading-none text-[var(--bp-accent-strong)] sm:text-6xl">
                市场规模
              </h2>
            </div>
            <p className="max-w-5xl text-2xl font-black leading-tight text-[var(--bp-text)] sm:text-3xl">
              市场规模与财务预测：极度务实的 7.5 亿交易盘口。
            </p>
          </header>

          <div className="grid gap-5 xl:grid-cols-[1.02fr_0.98fr] xl:items-start">
            <MarketBubbleChart />
            <FinanceModelPanel />
          </div>

          <TrendSignalStrip />

        </div>
      </SlideFrame>

      <SlideFrame id="business-model">
        <div className="relative z-10 grid w-full gap-7 px-6 sm:px-10 lg:px-14">
          <header className="grid gap-5 lg:grid-cols-[0.5fr_1fr] lg:items-end">
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">
                第 07 页 · 商业模式
              </p>
              <h2 className="mt-3 text-5xl font-black leading-none text-[var(--bp-accent-strong)] sm:text-6xl">
                商业模式
              </h2>
            </div>
            <p className="max-w-5xl text-2xl font-black leading-tight text-[var(--bp-text)] sm:text-3xl">
              交易抽佣贡献 75% 收入，订阅、广告增值和官方工作室直营补足平台变现结构。
            </p>
          </header>

          <RevenueModelOverview />

          <div className="grid gap-5 xl:grid-cols-[1.12fr_0.88fr] xl:items-start">
            <OrderGmvEngine />
            <SubscriptionModelPanel />
          </div>

        </div>
      </SlideFrame>

      <SlideFrame id="profit-estimate">
        <div className="relative z-10 grid w-full gap-7 px-6 sm:px-10 lg:px-14">
          <header className="grid gap-5 lg:grid-cols-[0.5fr_1fr] lg:items-end">
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">
                第 08 页 · 利润估算
              </p>
              <h2 className="mt-3 text-5xl font-black leading-none text-[var(--bp-accent-strong)] sm:text-6xl">
                利润估算
              </h2>
            </div>
            <p className="max-w-5xl text-2xl font-black leading-tight text-[var(--bp-text)] sm:text-3xl">
              7500 万平台收入，1500 万成本费用，对应约 5000 万净利润和 66% 净利润率。
            </p>
          </header>

          <div className="grid gap-5 xl:grid-cols-[0.92fr_1.08fr] xl:items-start">
            <CostBreakdownPanel />
            <ProfitEstimatePanel />
          </div>

        </div>
      </SlideFrame>

      <SlideFrame id="competition">
        <div className="relative z-10 grid w-full content-start gap-5 px-6 sm:px-9 lg:px-10">
          <header className="grid gap-5 lg:grid-cols-[0.42fr_1fr] lg:items-end">
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">
                第 09 页 · 竞争分析
              </p>
              <h2 className="mt-3 text-5xl font-black leading-none text-[var(--bp-accent-strong)]">
                竞争分析
              </h2>
            </div>
            <p className="max-w-5xl text-2xl font-black leading-tight text-[var(--bp-text)]">
              云作不是泛外包平台，也不是单一美术社区，而是 AIGC 原生交易基础设施。
            </p>
          </header>

          <CompetitiveMatrix />

        </div>
      </SlideFrame>

      <SlideFrame id="competition-conclusion">
        <div className="relative z-10 grid w-full content-start gap-5 px-6 sm:px-9 lg:px-10">
          <header className="grid gap-5 lg:grid-cols-[0.42fr_1fr] lg:items-end">
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">
                第 10 页 · 竞争结论
              </p>
              <h2 className="mt-3 text-5xl font-black leading-none text-[var(--bp-accent-strong)]">
                竞争结论
              </h2>
            </div>
            <p className="max-w-5xl text-2xl font-black leading-tight text-[var(--bp-text)]">
              海外平台验证交易闭环，传统威客验证供需规模，垂直社区验证专业粘性，云作把三者叠加到 AIGC 原生场景。
            </p>
          </header>

          <CompetitiveConclusionPanel />

        </div>
      </SlideFrame>

      <SlideFrame id="launch-strategy">
        <div className="relative z-10 grid w-full content-start gap-5 px-6 sm:px-9 lg:px-10">
          <header className="grid gap-5 lg:grid-cols-[0.42fr_1fr] lg:items-end">
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">
                第 11 页 · 启动策略
              </p>
              <h2 className="mt-3 text-5xl font-black leading-none text-[var(--bp-accent-strong)]">
                启动策略
              </h2>
            </div>
            <p className="max-w-5xl text-2xl font-black leading-tight text-[var(--bp-text)]">
              先用强运营跑通交易闭环，再用供给密度、案例和履约保障推动双边网络自增长。
            </p>
          </header>

          <LaunchStrategyPanel />

        </div>
      </SlideFrame>

      <SlideFrame id="founding-team">
        <div className="relative z-10 grid w-full content-start gap-5 px-6 sm:px-8 lg:px-9">
          <header>
            <h2 className="text-5xl font-black leading-none text-[var(--bp-accent-strong)]">
              创始团队
            </h2>
          </header>

          <FoundingTeamPanel />
        </div>
      </SlideFrame>

      <SlideFrame id="milestones">
        <div className="relative z-10 grid w-full content-start gap-5 px-6 sm:px-9 lg:px-10">
          <header className="grid gap-5 lg:grid-cols-[0.42fr_1fr] lg:items-end">
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">
                第 13 页 · 里程碑
              </p>
              <h2 className="mt-3 text-5xl font-black leading-none text-[var(--bp-accent-strong)]">
                里程碑
              </h2>
            </div>
            <p className="max-w-5xl text-2xl font-black leading-tight text-[var(--bp-text)]">
              从最小版本闭环验证，到三轮融资、供给扩张和第三年商业化目标，按曲线增长路径推进。
            </p>
          </header>

          <MilestoneTimelinePanel />
        </div>
      </SlideFrame>

      <SlideFrame id="funding-target">
        <div className="relative z-10 grid w-full content-start gap-5 px-6 sm:px-9 lg:px-10">
          <header className="grid gap-5 lg:grid-cols-[0.42fr_1fr] lg:items-end">
            <div>
              <p className="text-xs font-black uppercase tracking-normal text-[var(--bp-accent)]">
                第 14 页 · 融资目标
              </p>
              <h2 className="mt-3 text-5xl font-black leading-none text-[var(--bp-accent-strong)]">
                融资目标
              </h2>
            </div>
            <p className="max-w-5xl text-2xl font-black leading-tight text-[var(--bp-text)]">
              天使轮验证首批交易闭环，种子轮扩大供需两端，把月 GMV 推入增长轨道。
            </p>
          </header>

          <FundingTargetPanel />
        </div>
      </SlideFrame>
    </main>
  );
};

export default BusinessPlan;
